import { useEffect, useState } from 'react';
import { alarms, type AlarmProfile, type AlarmEvent, type AlarmProfileInput } from '../../api/alarms';
import { activeRole } from '../../stores/auth';

export function AlarmsPage() {
  const [profiles, setProfiles] = useState<AlarmProfile[]>([]);
  const [events, setEvents] = useState<AlarmEvent[]>([]);
  const [mqttUp, setMqttUp] = useState<boolean | null>(null);
  const [editing, setEditing] = useState<AlarmProfile | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const role = activeRole();
  const canEdit = role === 'owner' || role === 'admin';

  async function refresh() {
    try {
      const [p, e, s] = await Promise.all([
        alarms.profiles.list(),
        alarms.events.list(50),
        alarms.status(),
      ]);
      setProfiles(p.profiles);
      setEvents(e.events);
      setMqttUp(s.mqtt_connected);
    } catch { setErr('Failed to load alarms.'); }
  }
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15_000);
    return () => clearInterval(t);
  }, []);

  async function test(p: AlarmProfile) {
    if (!confirm(`Fire a TEST alarm "${p.name}"? All active screens will show the overlay and sirens will sound for ${p.siren_duration_s}s.`)) return;
    setErr(null); setInfo(null);
    try {
      const r = await alarms.profiles.test(p.id);
      setInfo(`Test alarm fired — reached ${r.deviceCount} device(s). Auto-clears in ${p.siren_duration_s}s.`);
      refresh();
    } catch (e: unknown) {
      const detail = (e as { detail?: { error?: string } }).detail?.error;
      setErr(detail === 'not_found_or_disabled' ? 'Profile not found or disabled.' : 'Test fire failed.');
    }
  }

  async function clear(evt: AlarmEvent) {
    if (!confirm('Manually clear this alarm now?')) return;
    try { await alarms.events.clear(evt.id); refresh(); }
    catch { setErr('Clear failed.'); }
  }

  async function remove(p: AlarmProfile) {
    if (!confirm(`Delete alarm profile "${p.name}"? History is preserved.`)) return;
    try { await alarms.profiles.remove(p.id); refresh(); }
    catch { setErr('Delete failed.'); }
  }

  const activeEvents = events.filter((e) => !e.cleared_at);

  return (
    <div>
      <div className="toolbar">
        <h1>Alarms</h1>
        <div style={{ color: 'var(--fg-3)', fontSize: '0.88em', marginLeft: '1em' }}>
          MQTT-triggered emergency alerts. On fire: overlay + optional GPIO relay.
        </div>
        <div className="spacer" />
        <MqttStatusPill up={mqttUp} />
        {canEdit && (
          <button className="primary" style={{ marginLeft: '0.6em' }} onClick={() => setEditing('new')}>
            + New profile
          </button>
        )}
      </div>

      {err && <div className="error">{err}</div>}
      {info && <div className="card" style={{ background: 'rgba(16, 185, 129, 0.1)', borderColor: 'var(--success, #10b981)' }}>{info}</div>}

      {activeEvents.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--danger)', borderWidth: 2, marginBottom: '1em' }}>
          <h3 style={{ marginTop: 0, color: 'var(--danger)' }}>Active alarms</h3>
          {activeEvents.map((e) => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', padding: '0.4em 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <strong>{e.profile_name}</strong>
                <span style={{ color: 'var(--fg-3)', marginLeft: '1em' }}>
                  Fired {new Date(e.triggered_at).toLocaleTimeString()} · {e.device_count} device(s) · {e.trigger_source}
                </span>
                <div style={{ color: 'var(--fg-2)', fontSize: '0.9em', marginTop: '0.2em' }}>{e.message_shown}</div>
              </div>
              {canEdit && <button className="danger" onClick={() => clear(e)}>Clear now</button>}
            </div>
          ))}
        </div>
      )}

      <h2>Profiles</h2>
      {profiles.length === 0 ? (
        <div className="empty">No alarm profiles yet. Create one to link an MQTT topic to a screen message + GPIO output.</div>
      ) : (
        <table>
          <thead><tr>
            <th>Name</th><th>MQTT topic</th><th>Message</th><th>Duration</th>
            <th>GPIO</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {profiles.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.name}</strong></td>
                <td style={{ fontFamily: 'monospace', color: 'var(--fg-2)' }}>{p.mqtt_topic}</td>
                <td>
                  <div style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: p.message_color, marginRight: '0.4em', verticalAlign: 'middle' }} />
                  {p.default_message.length > 60 ? p.default_message.slice(0, 60) + '…' : p.default_message}
                </td>
                <td>{p.siren_duration_s}s</td>
                <td>{p.drive_gpio ? 'On' : 'Off'}</td>
                <td>
                  {p.enabled ? <span className="badge active">enabled</span> : <span className="badge offline">disabled</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {canEdit && (
                    <>
                      <button onClick={() => test(p)} disabled={!p.enabled} title={p.enabled ? 'Fire a test alarm' : 'Enable to test'}>
                        Test
                      </button>{' '}
                      <button onClick={() => setEditing(p)}>Edit</button>{' '}
                      <button className="danger" onClick={() => remove(p)}>Delete</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 style={{ marginTop: '2em' }}>Event history</h2>
      {events.length === 0 ? (
        <div className="empty">No alarm events recorded yet.</div>
      ) : (
        <table>
          <thead><tr>
            <th>Fired at</th><th>Profile</th><th>Source</th><th>Message shown</th>
            <th>Devices</th><th>Cleared at</th><th>By</th>
          </tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.triggered_at).toLocaleString()}</td>
                <td><strong>{e.profile_name}</strong></td>
                <td>
                  <span className="badge" style={{ background: 'var(--bg-2)', color: 'var(--fg-2)' }}>
                    {e.trigger_source}
                  </span>
                </td>
                <td style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.message_shown}>
                  {e.message_shown}
                </td>
                <td>{e.device_count}</td>
                <td style={{ color: e.cleared_at ? 'var(--fg-2)' : 'var(--danger)' }}>
                  {e.cleared_at ? new Date(e.cleared_at).toLocaleString() : 'active'}
                </td>
                <td style={{ color: 'var(--fg-3)', fontSize: '0.85em' }}>{e.triggered_by_email ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditProfileModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function MqttStatusPill({ up }: { up: boolean | null }) {
  if (up === null) return null;
  const color = up ? 'var(--success, #10b981)' : 'var(--danger)';
  const bg = up ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)';
  return (
    <span style={{
      padding: '0.3em 0.7em', borderRadius: 12, fontSize: '0.82em',
      color, background: bg, fontFamily: 'monospace',
    }}>
      MQTT {up ? 'connected' : 'offline'}
    </span>
  );
}

function EditProfileModal({
  initial, onClose, onSaved,
}: { initial: AlarmProfile | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<AlarmProfileInput>({
    name: initial?.name ?? '',
    mqtt_topic: initial?.mqtt_topic ?? '',
    default_message: initial?.default_message ?? '',
    message_color: initial?.message_color ?? '#dc2626',
    siren_duration_s: initial?.siren_duration_s ?? 30,
    drive_gpio: initial?.drive_gpio ?? true,
    enabled: initial?.enabled ?? true,
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr(null); setBusy(true);
    try {
      if (initial) await alarms.profiles.update(initial.id, form);
      else await alarms.profiles.create(form);
      onSaved();
    } catch (e: unknown) {
      const detail = (e as { detail?: { error?: string } }).detail?.error;
      if (detail === 'topic_already_registered_in_org') {
        setErr('Another profile in this org already uses that MQTT topic.');
      } else if (detail === 'invalid_input') {
        setErr('Please check every field. Topic must be MQTT-legal characters only.');
      } else setErr('Save failed.');
    } finally { setBusy(false); }
  }

  const set = <K extends keyof AlarmProfileInput>(k: K, v: AlarmProfileInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 540 }}>
        <h2 style={{ marginTop: 0 }}>{initial ? 'Edit' : 'New'} alarm profile</h2>

        <div className="field">
          <label>Name</label>
          <input value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} autoFocus />
        </div>

        <div className="field">
          <label>MQTT topic</label>
          <input value={form.mqtt_topic ?? ''} onChange={(e) => set('mqtt_topic', e.target.value)}
            placeholder="iotech/alarm/fire" style={{ fontFamily: 'monospace' }} />
          <div style={{ color: 'var(--fg-3)', fontSize: '0.82em', marginTop: '0.3em' }}>
            Any published message to this topic triggers the alarm. Send an empty message
            to <code>{form.mqtt_topic || '<topic>'}/clear</code> to end it early.
          </div>
        </div>

        <div className="field">
          <label>Display message (default)</label>
          <textarea value={form.default_message ?? ''} onChange={(e) => set('default_message', e.target.value)}
            rows={2} placeholder="EMERGENCY — EVACUATE IMMEDIATELY" />
          <div style={{ color: 'var(--fg-3)', fontSize: '0.82em', marginTop: '0.3em' }}>
            MQTT payloads override this. Send plain text or JSON <code>{'{"message":"..."}'}</code>.
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1em' }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Message color</label>
            <input type="color" value={form.message_color ?? '#dc2626'}
              onChange={(e) => set('message_color', e.target.value)} style={{ width: 60, height: 32 }} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Siren duration (s)</label>
            <input type="number" min={1} max={3600} value={form.siren_duration_s ?? 30}
              onChange={(e) => set('siren_duration_s', Number(e.target.value))} />
          </div>
        </div>

        <div className="field">
          <label>
            <input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
              checked={form.drive_gpio ?? true} onChange={(e) => set('drive_gpio', e.target.checked)} />
            Drive GPIO relay (turn on siren hardware)
          </label>
        </div>
        <div className="field">
          <label>
            <input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
              checked={form.enabled ?? true} onChange={(e) => set('enabled', e.target.checked)} />
            Enabled (unchecked profiles ignore MQTT messages)
          </label>
        </div>

        {err && <div className="error">{err}</div>}

        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end', marginTop: '1em' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !form.name || !form.mqtt_topic || !form.default_message} onClick={submit}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
